import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useUserScope } from "@/hooks/useUserScope";
import { useAuth } from "@/contexts/AuthContext";
import { ContactFilterSidebar } from "./ContactFilterSidebar";
import { DncBadge } from "@/components/dnc/DncBadge";
import { ConnectedBadge } from "@/components/connections/ConnectedBadge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Shield, Linkedin, Users, Download, MoreHorizontal, Megaphone, Plus, UserCheck, Building2, Sparkles } from "lucide-react";
import { UnifiedContactModal } from "@/components/contacts/UnifiedContactModal";
import { LinkedInFollowUpPanel } from "@/components/contacts/panels/LinkedInFollowUpPanel";
import type { CampaignOutreachContact } from "@/types/outreach";
import { toast } from "sonner";
import { openExternal } from "@/lib/utils";

interface AccountContactsTabProps {
  accountId: string;
  companyName?: string;
  companyDomain?: string;
}

interface FamilyAccount {
  id: string;
  name: string;
  relationship: "self" | "parent" | "child" | "sibling";
}

function getSeniorityColor(s: string | null) {
  switch (s) {
    case "C-Suite": return "bg-primary/10 text-primary border-primary/20";
    case "Director": return "bg-blue-500/10 text-blue-600 border-blue-500/20";
    case "Manager": return "bg-green-500/10 text-green-600 border-green-500/20";
    default: return "bg-muted text-muted-foreground border-border";
  }
}

export function AccountContactsTab({ accountId, companyName, companyDomain }: AccountContactsTabProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { personaId } = useUserScope();
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showFamilyContacts, setShowFamilyContacts] = useState(true);

  // Fetch account family info (parent/children/siblings)
  const { data: familyInfo } = useQuery({
    queryKey: ["company-family", accountId],
    queryFn: async () => {
      const { data: self } = await supabase
        .from("companies")
        .select("id, company_name, parent_company_id")
        .eq("id", accountId)
        .single();
      const selfData = self as { id: string; company_name: string; parent_company_id: string | null } | null;
      if (!selfData) return { hasFamily: false, isChild: false, accounts: [], accountMap: {} as Record<string, FamilyAccount> };

      const familyAccounts: FamilyAccount[] = [
        { id: selfData.id, name: selfData.company_name, relationship: "self" },
      ];

      if (selfData.parent_company_id) {
        const { data: parent } = await supabase
          .from("companies")
          .select("id, company_name")
          .eq("id", selfData.parent_company_id)
          .single();
        const p = parent as { id: string; company_name: string } | null;
        if (p) familyAccounts.push({ id: p.id, name: p.company_name, relationship: "parent" });

        const { data: siblings } = await supabase
          .from("companies")
          .select("id, company_name")
          .eq("parent_company_id", selfData.parent_company_id)
          .neq("id", accountId);
        (siblings as { id: string; company_name: string }[] | null)?.forEach((s) =>
          familyAccounts.push({ id: s.id, name: s.company_name, relationship: "sibling" })
        );
      } else {
        const { data: children } = await supabase
          .from("companies")
          .select("id, company_name")
          .eq("parent_company_id", accountId);
        (children as { id: string; company_name: string }[] | null)?.forEach((c) =>
          familyAccounts.push({ id: c.id, name: c.company_name, relationship: "child" })
        );
      }

      return {
        hasFamily: familyAccounts.length > 1,
        isChild: !!selfData.parent_company_id,
        accounts: familyAccounts,
        accountMap: Object.fromEntries(familyAccounts.map((a) => [a.id, a])) as Record<string, FamilyAccount>,
      };
    },
  });

  const hasFamily = familyInfo?.hasFamily ?? false;
  const queryCompanyIds = useMemo(() => {
    if (!showFamilyContacts || !familyInfo?.hasFamily) return [accountId];
    return familyInfo.accounts.map((a) => a.id);
  }, [accountId, showFamilyContacts, familyInfo]);

  // Contact Intelligence modal state
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactModalId, setContactModalId] = useState<string | null>(null);

  // Context-Aware Outreach Modal state (drafting outreach from a contact row)
  const [draftOutreachOpen, setDraftOutreachOpen] = useState(false);
  const [draftOutreachContact, setDraftOutreachContact] = useState<CampaignOutreachContact | null>(null);

  const openDraftOutreach = (contact: Record<string, any>) => {
    setDraftOutreachContact({
      contact_id: contact.id,
      first_name: contact.first_name ?? null,
      last_name: contact.last_name ?? null,
      full_name: contact.full_name ?? null,
      job_title: contact.job_title ?? null,
      company_name: contact.company_name ?? companyName ?? null,
      company_id: contact.company_id ?? accountId,
      account_domain: contact.account_domain ?? companyDomain ?? null,
      email: contact.email ?? null,
      linkedin_url: contact.linkedin_url ?? null,
      headline: contact.headline ?? null,
      seniority_level: contact.seniority_level ?? null,
      department: contact.department ?? null,
      location: contact.location ?? null,
    });
    setDraftOutreachOpen(true);
  };

  // Dialog state
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [actionContacts, setActionContacts] = useState<any[]>([]);

  // Campaign dialog state
  const [selectedCampaignId, setSelectedCampaignId] = useState<string>("");
  const [newCampaignName, setNewCampaignName] = useState("");
  const [newCampaignPlatform, setNewCampaignPlatform] = useState("email");
  const [newCampaignSenderId, setNewCampaignSenderId] = useState<string>("");
  const [campaignMode, setCampaignMode] = useState<"existing" | "new">("existing");
  const [campaignSubmitting, setCampaignSubmitting] = useState(false);

  const { data: contacts, isLoading } = useQuery({
    queryKey: ["account-contacts-tab", accountId, queryCompanyIds, search, filterValues],
    queryFn: async () => {
      let query = supabase.from("contacts").select("*").in("company_id", queryCompanyIds).not('full_name', 'is', null).neq('full_name', '').order("full_name");
      if (search) query = query.or(`full_name.ilike.%${search}%,job_title.ilike.%${search}%,email.ilike.%${search}%`);
      if (filterValues.seniority?.length) query = query.in("seniority_level", filterValues.seniority);
      if (filterValues.department?.length) query = query.in("department", filterValues.department);
      if (filterValues.source?.length) query = query.in("company_id", filterValues.source);
      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const { data: campaigns } = useQuery({
    queryKey: ["active-campaigns"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("campaigns")
        .select("id, name, status, platform")
        .in("status", ["active", "draft"])
        .order("name");
      if (error) throw error;
      return data;
    },
    enabled: campaignDialogOpen,
  });

  const { data: sendingPersonas } = useQuery({
    queryKey: ["sending-personas"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sending_personas")
        .select("id, full_name")
        .order("full_name");
      if (error) throw error;
      return data;
    },
    enabled: campaignDialogOpen && !personaId,
  });

  const { data: filterOptions } = useQuery({
    queryKey: ["account-contacts-filters", accountId, queryCompanyIds],
    queryFn: async () => {
      const { data, error } = await supabase.from("contacts").select("seniority_level, department, company_id").in("company_id", queryCompanyIds);
      if (error) throw error;
      const seniorityCounts: Record<string, number> = {};
      const departmentCounts: Record<string, number> = {};
      const sourceCounts: Record<string, number> = {};
      data?.forEach((c) => {
        if (c.seniority_level) seniorityCounts[c.seniority_level] = (seniorityCounts[c.seniority_level] || 0) + 1;
        if (c.department) departmentCounts[c.department] = (departmentCounts[c.department] || 0) + 1;
        if (c.company_id) sourceCounts[c.company_id] = (sourceCounts[c.company_id] || 0) + 1;
      });
      return { seniorityCounts, departmentCounts, sourceCounts };
    },
  });

  const filterSections = useMemo(() => {
    const sections: { id: string; label: string; options: { value: string; label: string; count: number }[] }[] = [];
    if (showFamilyContacts && hasFamily && filterOptions?.sourceCounts && Object.keys(filterOptions.sourceCounts).length > 1) {
      sections.push({ id: "source", label: "Source Account", options: Object.entries(filterOptions.sourceCounts).sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, label: familyInfo?.accountMap[value]?.name || value.slice(0, 8), count })) });
    }
    if (filterOptions?.seniorityCounts && Object.keys(filterOptions.seniorityCounts).length > 0) {
      sections.push({ id: "seniority", label: "Seniority Level", options: Object.entries(filterOptions.seniorityCounts).sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, label: value, count })) });
    }
    if (filterOptions?.departmentCounts && Object.keys(filterOptions.departmentCounts).length > 0) {
      sections.push({ id: "department", label: "Department", options: Object.entries(filterOptions.departmentCounts).sort((a, b) => b[1] - a[1]).map(([value, count]) => ({ value, label: value, count })) });
    }
    return sections;
  }, [filterOptions, showFamilyContacts, hasFamily, familyInfo]);

  const hasActiveFilters = search.length > 0 || Object.values(filterValues).some((v) => v.length > 0);

  const totalContacts = contacts?.length || 0;
  const withEmail = contacts?.filter((c) => c.email).length || 0;
  const seniorContacts = contacts?.filter((c) => ["C-Suite", "Director", "VP"].includes(c.seniority_level || "")).length || 0;
  const emailCoverage = totalContacts > 0 ? Math.round((withEmail / totalContacts) * 100) : 0;

  // Selection helpers
  const allSelected = contacts && contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));
  const someSelected = selectedIds.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts?.map((c) => c.id) || []));
    }
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const getSelectedContacts = () => contacts?.filter((c) => selectedIds.has(c.id)) || [];

  // Open campaign dialog
  const openCampaignDialog = (contactList: any[], mode: "existing" | "new" = "existing") => {
    setActionContacts(contactList);
    setSelectedCampaignId("");
    setNewCampaignName("");
    setCampaignMode(mode);
    setCampaignDialogOpen(true);
  };

  // Submit: Add to Campaign
  const handleAddToCampaign = async () => {
    setCampaignSubmitting(true);
    try {
      let campaignId = selectedCampaignId;

      if (campaignMode === "new") {
        if (!newCampaignName.trim()) { toast.error("Campaign name required"); setCampaignSubmitting(false); return; }
        const resolvedPersonaId = personaId || newCampaignSenderId || null;
        if (!resolvedPersonaId) { toast.error("Please select a sender"); setCampaignSubmitting(false); return; }
        const { data, error } = await supabase
          .from("campaigns")
          .insert({ name: newCampaignName.trim(), platform: newCampaignPlatform, status: "planning", sending_persona_id: resolvedPersonaId, ...(user?.id ? { created_by_user_id: user.id } : {}) })
          .select("id")
          .single();
        if (error) throw error;
        campaignId = data.id;
      }

      if (!campaignId) { toast.error("Select a campaign"); setCampaignSubmitting(false); return; }

      const inserts = actionContacts.map((c) => ({
        campaign_id: campaignId,
        contact_id: c.id,
        engagement_status: "pending",
        enrolled_at: new Date().toISOString(),
      }));

      const { error } = await supabase.from("campaign_contacts").insert(inserts);
      if (error) throw error;

      toast.success(`${actionContacts.length} contact(s) added to campaign`);
      setCampaignDialogOpen(false);
      setSelectedIds(new Set());
      queryClient.invalidateQueries({ queryKey: ["active-campaigns"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to add to campaign");
    } finally {
      setCampaignSubmitting(false);
    }
  };

  if (isLoading) return <div className="flex items-center justify-center h-[400px]"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-0">
      {/* Contact Management Header */}
      <div className="p-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <Mail className="h-5 w-5" /> Contact Management
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {someSelected && (
              <>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => openCampaignDialog(getSelectedContacts())}>
                  <Megaphone className="h-4 w-4" /> Add to Campaign ({selectedIds.size})
                </Button>
                <Button variant="outline" size="sm" className="gap-2" onClick={() => openCampaignDialog(getSelectedContacts(), "new")}>
                  <Plus className="h-4 w-4" /> Create Campaign ({selectedIds.size})
                </Button>
              </>
            )}
            <Button variant="outline" size="sm" className="gap-2">
              <Download className="h-4 w-4" /> Export
            </Button>
          </div>
        </div>
        {/* Summary metric cards */}
        <div className="grid grid-cols-4 gap-3">
          <Card className="bg-blue-50/50">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-blue-700">{totalContacts}</p>
              <p className="text-xs text-muted-foreground">Total Contacts</p>
            </CardContent>
          </Card>
          <Card className="bg-green-50/50">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-green-700">{withEmail}</p>
              <p className="text-xs text-muted-foreground">With Email</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50/50">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-purple-700">{seniorContacts}</p>
              <p className="text-xs text-muted-foreground">Senior Contacts</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50/50">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold text-amber-700">{emailCoverage}%</p>
              <p className="text-xs text-muted-foreground">Email Coverage</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Family toggle bar */}
      {hasFamily && (
        <div className="flex items-center gap-3 px-6 py-2.5 border-b bg-muted/30">
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <div className="flex items-center gap-2">
            <Switch
              id="carr-family-toggle"
              checked={showFamilyContacts}
              onCheckedChange={setShowFamilyContacts}
            />
            <Label htmlFor="carr-family-toggle" className="text-sm text-muted-foreground cursor-pointer">
              Show contacts from {familyInfo?.isChild ? "parent & sibling" : "subsidiary"} accounts
            </Label>
          </div>
          {showFamilyContacts && (
            <span className="text-xs text-muted-foreground ml-auto">
              {familyInfo?.accounts.length} linked accounts
            </span>
          )}
        </div>
      )}

      {/* Filter + Table */}
      <div className="flex h-[500px]">
        <ContactFilterSidebar
          totalCount={totalContacts}
          searchValue={search}
          onSearchChange={setSearch}
          filterSections={filterSections}
          filterValues={filterValues}
          onFilterChange={(sectionId, values) => setFilterValues((prev) => ({ ...prev, [sectionId]: values }))}
          onClearFilters={() => { setFilterValues({}); setSearch(""); }}
          hasActiveFilters={hasActiveFilters}
        />
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
                </TableHead>
                <TableHead className="w-[180px]">Name</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Seniority</TableHead>
                <TableHead>Department</TableHead>
                {showFamilyContacts && hasFamily && <TableHead>Source</TableHead>}
                <TableHead>Email</TableHead>
                <TableHead>LinkedIn</TableHead>
                <TableHead className="w-[60px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contacts?.length === 0 ? (
                <TableRow><TableCell colSpan={showFamilyContacts && hasFamily ? 9 : 8} className="text-center py-12"><Users className="h-8 w-8 mx-auto text-muted-foreground mb-2" /><p className="text-sm text-muted-foreground">No contacts found</p></TableCell></TableRow>
              ) : (
                contacts?.map((contact) => (
                  <TableRow key={contact.id} className={selectedIds.has(contact.id) ? "bg-primary/5" : ""}>
                    <TableCell>
                      <Checkbox checked={selectedIds.has(contact.id)} onCheckedChange={() => toggleOne(contact.id)} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-primary cursor-pointer hover:underline" onClick={() => { setContactModalId(contact.id); setContactModalOpen(true); }}>{contact.full_name}</span>
                        {contact.is_decision_maker && <span title="Decision Maker"><Shield className="h-3.5 w-3.5 text-primary" /></span>}
                        <DncBadge contactId={contact.id} accountId={contact.company_id} iconOnly />
                        <ConnectedBadge contactId={contact.id} iconOnly />
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{contact.job_title || "—"}</TableCell>
                    <TableCell>{contact.seniority_level ? <Badge variant="outline" className={getSeniorityColor(contact.seniority_level)}>{contact.seniority_level}</Badge> : "—"}</TableCell>
                    <TableCell className="text-muted-foreground">{contact.department || "—"}</TableCell>
                    {showFamilyContacts && hasFamily && (
                      <TableCell>
                        {contact.company_id !== accountId ? (
                          <button
                            onClick={() => navigate(`/accounts/${contact.company_id}`)}
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                            title={`View ${familyInfo?.accountMap[contact.company_id]?.name || 'account'}`}
                          >
                            <Building2 className="h-3 w-3" />
                            <span className="truncate max-w-[120px]">{familyInfo?.accountMap[contact.company_id]?.name || '—'}</span>
                          </button>
                        ) : (
                          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-xs">Direct</Badge>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="flex items-center gap-1.5 text-primary hover:underline">
                          <Mail className="h-3.5 w-3.5" />
                          <span className="truncate max-w-[160px]">{contact.email}</span>
                        </a>
                      ) : <span className="text-muted-foreground text-xs">No email</span>}
                    </TableCell>
                    <TableCell>
                      {contact.linkedin_url ? (
                        <button type="button" onClick={() => openExternal(contact.linkedin_url)}>
                          <Linkedin className="h-4 w-4 text-blue-600 hover:text-blue-800" />
                        </button>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setContactModalId(contact.id); setContactModalOpen(true); }}>
                            <UserCheck className="h-4 w-4 mr-2" /> View Contact
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openDraftOutreach(contact)}>
                            <Sparkles className="h-4 w-4 mr-2" /> Draft Outreach
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openCampaignDialog([contact])}>
                            <Megaphone className="h-4 w-4 mr-2" /> Add to Campaign
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openCampaignDialog([contact], "new")}>
                            <Plus className="h-4 w-4 mr-2" /> Create Campaign
                          </DropdownMenuItem>
                          {contact.email && (
                            <DropdownMenuItem onClick={() => { navigator.clipboard.writeText(contact.email); toast.success("Email copied"); }}>
                              <Mail className="h-4 w-4 mr-2" /> Copy Email
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Add to Campaign Dialog */}
      <Dialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{campaignMode === "new" ? "Create Campaign" : "Add to Campaign"}</DialogTitle>
            <DialogDescription>
              {campaignMode === "new" ? "Create a new campaign with" : "Add"} {actionContacts.length} contact(s){campaignMode !== "new" ? " to a campaign" : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex gap-2">
              <Button size="sm" variant={campaignMode === "existing" ? "default" : "outline"} onClick={() => setCampaignMode("existing")}>
                Existing Campaign
              </Button>
              <Button size="sm" variant={campaignMode === "new" ? "default" : "outline"} onClick={() => setCampaignMode("new")}>
                New Campaign
              </Button>
            </div>

            {campaignMode === "existing" ? (
              <div className="space-y-2">
                <Label>Select Campaign</Label>
                <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                  <SelectTrigger><SelectValue placeholder="Choose a campaign..." /></SelectTrigger>
                  <SelectContent>
                    {campaigns?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name} <span className="text-muted-foreground ml-1">({c.status})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label>Campaign Name</Label>
                  <Input value={newCampaignName} onChange={(e) => setNewCampaignName(e.target.value)} placeholder="e.g. Q1 Outreach" />
                </div>
                <div>
                  <Label>Platform</Label>
                  <Select value={newCampaignPlatform} onValueChange={setNewCampaignPlatform}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="linkedin">LinkedIn</SelectItem>
                      <SelectItem value="multi-channel">Multi-Channel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {!personaId && sendingPersonas && sendingPersonas.length > 0 && (
                  <div>
                    <Label>Assigned Sender *</Label>
                    <Select value={newCampaignSenderId} onValueChange={setNewCampaignSenderId}>
                      <SelectTrigger><SelectValue placeholder="Select sender..." /></SelectTrigger>
                      <SelectContent>
                        {sendingPersonas.map((sp) => (
                          <SelectItem key={sp.id} value={sp.id}>{sp.full_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCampaignDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddToCampaign} disabled={campaignSubmitting}>
              {campaignSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {campaignMode === "new" ? "Create & Add" : "Add to Campaign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Detail Modal */}
      <UnifiedContactModal
        contactId={contactModalId}
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
      />

      {draftOutreachOpen && draftOutreachContact?.contact_id && (
        <UnifiedContactModal
          contactId={draftOutreachContact.contact_id}
          open={draftOutreachOpen}
          onOpenChange={setDraftOutreachOpen}
          mode="linkedin"
          panel={<LinkedInFollowUpPanel contactId={draftOutreachContact.contact_id} />}
        />
      )}
    </div>
  );
}

// Pipeline / opportunity dialogs removed — sales pipeline is out of app scope.
